import { WagoApiClient } from '@shared/service/wago-api.client';
import { ElbridgeHookVO } from '../vo/elbridge-hook.vo';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { ProjectItemListHelper } from '@project/helper/project-item-list-helper';
import { ElbridgeDataVO } from '../vo/elbridge-data.vo';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleNode } from '../../../article/presenter/article-node';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ProjectUtils } from '../../util/project-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleAttributeUtils } from '@article/util/article-attribute.utils';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { HttpParams } from '@angular/common/http';
import { ProjectVO } from '@project/model/vo/project.vo';
import { ElbridgeDataContainerVO } from '../vo/elbridge-data-container.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectOrderingUtils } from '../../util/project-ordering-utils';
import { Injectable } from '@angular/core';
import { QueryParamsService } from '@services/query-params/query-params.service';
import { UserActionsLoggingManager } from '@user/service/logging/user-actions-logging.manager';
import { AlertHelper } from '@shared/alert/alert.helper';

@Injectable({ providedIn: 'root' })
export class ElbridgeService {
  public static SUPPLIER_ID_GLN: string = '4017332000000';

  private _elbridgeHookChanged = new Subject<void>();
  private _elbridgeHook?: ElbridgeHookVO;

  constructor(
    private modelConversionService: ModelConversionService,
    private wagoApiClient: WagoApiClient,
    private projectItemListHelper: ProjectItemListHelper,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private queryParamsService: QueryParamsService,
    private userActionsLoggingManager: UserActionsLoggingManager,
    private alertHelper: AlertHelper
  ) {
  }

  public get elbridgeHookChanged$(): Observable<void> {
    return this._elbridgeHookChanged.asObservable();
  }

  public get elbridgeHook(): ElbridgeHookVO | undefined {
    return this._elbridgeHook;
  }

  public get elbridgeHookExists(): boolean {
    return !!this.elbridgeHook;
  }

  public async initElbridge(): Promise<void> {
    if (!this.queryParamsService.hasFunction('elbridge')) {
      return;
    }

    this.userActionsLoggingManager.logFunction('function.elbridge');
    const refId = this.queryParamsService.getParamValue('refid');
    if (!refId) {
      this.showNoElbridgeHookAlert();
      return;
    }

    this._elbridgeHook = await this.getElbridgeHook(refId).catch(() => undefined);
    if(!this._elbridgeHook){
      this.showNoElbridgeHookAlert();
      return;
    }

    this.notifyElbridgeHookChanged();
  }

  private showNoElbridgeHookAlert(): void {
    this.alertHelper.showError('elbridge.no_hook.title', 'elbridge.no_hook.text');
  }

  private getElbridgeHook(refId: string): Promise<ElbridgeHookVO> {
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('refId', refId);
    const requestOptions = { params: httpParams };

    return lastValueFrom(this.wagoApiClient.get('elbridge/hook/', requestOptions).pipe(
      map((response) => this.modelConversionService.convertElbridgeHookVO(response))
    ));
  }

  public sendElbridgeRequest(requestQuantity: number, project?: ProjectVO, elbridgeInterfaceURL?: string): void {
    let elbridgeHook = this._elbridgeHook;
    if (!elbridgeHook && elbridgeInterfaceURL) {
      elbridgeHook = new ElbridgeHookVO();
      elbridgeHook.hookUrl = elbridgeInterfaceURL;
      elbridgeHook.version = '2.0';
    }

    if (!elbridgeHook) {
      return;
    }

    if (this._elbridgeHook && !elbridgeInterfaceURL) {
      this._elbridgeHook = undefined;
    }

    const elbridgeData = this.createElbridgeData(requestQuantity);
    let resultValue = '';
    // test Version to decide whether to use container or not
    if (elbridgeHook.version && elbridgeHook.version.substr(0, 1) === '2') {
      const container = new ElbridgeDataContainerVO();
      container.CONFIGURATION_URL = project ? ProjectOrderingUtils.prepareDirectLink(project) : this.getCurrentURL();
      container.ITEM = elbridgeData;
      container.MIME = {};
      resultValue = JSON.stringify(container);
    } else {
      resultValue = JSON.stringify(elbridgeData);
    }
    // replace this specific key name since it is not well formed and Json property name seems to not be applied here
    resultValue = resultValue.replace(/UDX_EDXF_DISCOUNT_GROUP_MANUFACTURER/g, 'UDX.EDXF.DISCOUNT_GROUP_MANUFACTURER');
    const langKey: string = this.getEldbridgeLocaleKey();
    // currently country will remain DE as we have no other country version (only language)
    this.post(resultValue, elbridgeHook, langKey);
    if (this._elbridgeHook) {
      this._elbridgeHook = undefined;
    }
    this.notifyElbridgeHookChanged();
  }

  private createElbridgeData(requestQuantity: number): ElbridgeDataVO[] {
    const articleNodes = this.projectItemListHelper.getItemNodeList();
    let result: ElbridgeDataVO[] = [];
    articleNodes.forEach(article => {
      const elbridgeVos = this.convertToElbridgeVos(requestQuantity, article);
      result = result.concat(elbridgeVos);
    });

    return result;
  }

  private convertToElbridgeVos(requestQuantity: number, articleNode: ArticleNode): ElbridgeDataVO[] {
    let result: ElbridgeDataVO[] = [];
    if (!this.isArticleExcluded(articleNode.article)) {
      const vo: ElbridgeDataVO = new ElbridgeDataVO();
      vo.SUPPLIER_ID_GLN = ElbridgeService.SUPPLIER_ID_GLN;
      vo.MANUFACTURER_PID = articleNode.articleNo;
      if (articleNode.article.eanNo) {
        vo.INTERNATIONAL_PID = '' + articleNode.article.eanNo;
      }
      // add something like this to transmit price information for articles
      /*vo.CURRENCY = 'EUR';
      vo.PRICE_QUANTITY = '1';
      vo.PRICE_AMOUNT = articleNode.price.toFixed(2);
      vo.UDX_EDXF_DISCOUNT_GROUP_MANUFACTURER = 'no_discount_group';
      */
      vo.QUANTITY = '' + this.calculateQuantity(requestQuantity, articleNode.article, articleNode.articleListArticleUsageIds);
      vo.ORDER_UNIT = 'C62';
      result.push(vo);
    }

    if (articleNode.children == null) {
      return result;
    }

    articleNode.children.forEach(node => {
      const elbridgeVos = this.convertToElbridgeVos(requestQuantity, node);
      if (elbridgeVos.length > 0) {
        result = result.concat(elbridgeVos);
      }
    });
    return result;
  }

  private isArticleExcluded(article: ArticleVO): Boolean {
    return article.type.id === ArticleTypeIds.WIREBRIDGE
      || !ArticleUtils.containsDefaultWagoArticleNo(article.articleNo);
  }

  private calculateQuantity(requestQuantity: number, article: ArticleVO, articleListArticleUsageIds: string[]): number {
    if (ArticleUtils.isMountingRail(article)) {
      return articleListArticleUsageIds.length * requestQuantity;
    }
    const maxLength = PropertyUtils.getIntPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.MAX_LENGTH);
    const	usedLength = this.calculateArticleLength(articleListArticleUsageIds);
    if (maxLength > 0 && usedLength > 0) {
      return Math.round(((usedLength * requestQuantity) / maxLength) + 0.5);
    }
    const maxUnit = PropertyUtils.getIntPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.UNIT_COUNT);
    if (maxUnit > 0) {
      return Math.round(((articleListArticleUsageIds.length * requestQuantity) / maxUnit) + 0.5);
    }
    return articleListArticleUsageIds.length * requestQuantity;
  }

  private calculateArticleLength(articleListArticleUsageIds: string[]): number {
    let usedLengthSum: number = 0;
    articleListArticleUsageIds.forEach( articleUsageId => {
      const articleUsage = ProjectUtils.findArticleUsageInProjectById(this.projectModel.currentProject, articleUsageId);
      if (articleUsage == null) {
        return;
      }
      const usedLength: number = PropertyUtils.getNumberPropertyValue(articleUsage.infoProperties, 'articleLength');
      if (usedLength > 0) {
        usedLengthSum += usedLength;
      } else {
        const articleInfo = ArticleUtils.findArticleInfoByGuid(articleUsageId, this.projectModel.currentProject);
        const values = ArticleAttributeUtils.getAttributeValueFromArticleInfo(articleInfo, ArticleContainerPropertyDef.STRETCH_LENGTH);
        if (values != null) {
          usedLengthSum += parseInt(values[0], 10);
        }
      }
    });
    return usedLengthSum * 0.1;
  }

  private post(result: string, elbridgeHook: ElbridgeHookVO, language: string = 'deu', country: string = 'DE') {
    const mapForm = document.createElement('form');
    mapForm.id = 'elbridgeForm';
    mapForm.target = '_blank';
    mapForm.method = 'POST';

    const mapVersion = document.createElement('input');
    mapVersion.type = 'hidden';
    mapVersion.name = 'version';
    mapVersion.setAttribute('value', elbridgeHook.version);
    mapForm.appendChild(mapVersion);

    const mapResult = document.createElement('input');
    mapResult.type = 'hidden';
    mapResult.name = 'result';
    mapResult.setAttribute('value', result);
    mapForm.appendChild(mapResult);

    const mapUrl = document.createElement('input');
    mapUrl.type = 'hidden';
    mapUrl.name = 'hookUrl';
    mapUrl.setAttribute('value', elbridgeHook.hookUrl);
    mapForm.appendChild(mapUrl);

    const mapCountry = document.createElement('input');
    mapCountry.type = 'hidden';
    mapCountry.name = 'country';
    mapCountry.setAttribute('value', country);
    mapForm.appendChild(mapCountry);

    const mapLang = document.createElement('input');
    mapLang.type = 'hidden';
    mapLang.name = 'language';
    mapLang.setAttribute('value', language);
    mapForm.appendChild(mapLang);

    document.body.appendChild(mapForm);
    this.wagoApiClient.submitForm('elbridge/elbridgeReturn', mapForm);

    document.body.removeChild(mapForm);
  }

  private getCurrentURL(): string {
    return window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '') + window.location.pathname;
  }

  private getEldbridgeLocaleKey(): string {
    const languageKey = this.localeResourceManager.getValueByKey('elbridge.language.key');
    if (languageKey) {
      return languageKey;
    }

    // if language keys are not usable, add something like this to convert our language key into the requested ISO key:
    const currentKey = this.localeResourceManager.currentLanguage.langKey;
    switch (currentKey) {
      case 'nl_NL':
        return 'nld';
      case 'zh_CN':
        return 'chi';
      case 'ja_JP':
        return 'jap';
      case 'en_EN':
        return 'eng';
      case 'en_US':
        return 'eng';
      default:
        return 'deu';
    }
  }

  private notifyElbridgeHookChanged(): void {
    this._elbridgeHookChanged.next(undefined);
  }
}
